"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Button } from "@/core/components/ui/button";
import type { ScheduleListSlotProps } from "@/core/components/floating-chatbot/slots";
import { ScheduleEmptyState } from "../components/schedule-empty-state";
import { ScheduleListItem } from "../components/schedule-list-item";
import { useReportScheduleMutations } from "../hooks/use-report-schedule-mutations";
import { useReportSchedules } from "../hooks/use-report-schedules";
import {
  CHAT_TRACKING,
  ScheduleTrackingProvider,
  useScheduleTracking,
} from "../tracking";
import { NotificationSchedule } from "../types";
import { ScheduleFormSlot } from "./schedule-form-slot";

/**
 * Slot do block `schedule_list`: renderiza os agendamentos com o mesmo `ScheduleListItem`
 * da sheet — daí o visual, os ícones, o "Próximo envio" e as 3 ações (com o dialog de
 * confirmação do excluir) saírem de graça, nos 3 locales.
 *
 * As ações vão direto ao woba-backend pelas mutations existentes; o agente não participa.
 */
interface ScheduleListSlotInternalProps extends ScheduleListSlotProps {
  /**
   * Ignora o cache e renderiza exatamente os `schedules` recebidos.
   *
   * Necessário para o gate de "já existe um agendamento": lá a lista é lida com
   * `fetchQuery`, que **popula** `["report-schedules"]` antes de renderizar — o cache nunca
   * está vazio nesse caminho, então o fallback por `cached.length` descartaria em silêncio
   * justamente os agendamentos que motivaram o bloqueio.
   */
  preferGiven?: boolean;
}

export function ScheduleListSlot(props: ScheduleListSlotInternalProps) {
  return (
    <ScheduleTrackingProvider value={CHAT_TRACKING}>
      <ScheduleListSlotContent {...props} />
    </ScheduleTrackingProvider>
  );
}

function ScheduleListSlotContent({
  schedules,
  preferGiven = false,
}: ScheduleListSlotInternalProps) {
  // O cache é a fonte preferida: reflete alterações feitas na sheet (ou pelas ações abaixo)
  // sem depender de um novo turno do agente. `schedules` do block é o seed/fallback para
  // quando a query ainda não populou.
  const { schedules: cached } = useReportSchedules();
  const given = schedules as unknown as NotificationSchedule[];
  const items = (
    preferGiven || cached.length === 0 ? given : cached
  ) as NotificationSchedule[];

  const [translate] = useTranslation(["insights"]);
  const tracking = useScheduleTracking();
  const { remove, toggle } = useReportScheduleMutations();
  const [editing, setEditing] = useState<NotificationSchedule | null>(null);
  // Excluiu o último agendamento *neste card*: o resultado da ação precisa ficar visível.
  // Sem isso o card inteiro sumia da conversa e sobrava só o texto do agente.
  const [deletedLast, setDeletedLast] = useState(false);
  // CTA do estado vazio: abre o form de criação no lugar da lista.
  const [creating, setCreating] = useState(false);

  // Criar reusa o slot do form (sem `schedule` = criação), como o "Novo" da sheet.
  if (creating) {
    return (
      <ScheduleFormSlot
        schedule={null}
        onDone={() => setCreating(false)}
        fromList
      />
    );
  }

  // Editar reusa o slot do form, em modo edição, no lugar da lista.
  if (editing) {
    return (
      <ScheduleFormSlot
        schedule={editing as unknown as Record<string, unknown>}
        onDone={() => setEditing(null)}
        fromList
      />
    );
  }

  // Excluiu o último aqui → mostra o vazio, com o CTA de criar (senão o usuário fica sem
  // saída no card). Precede a checagem de `items` porque com `preferGiven` a lista continua
  // vindo da prop do block (congelada no que o agente viu) e nunca esvazia sozinha.
  if (deletedLast) {
    return (
      <div className="flex flex-col gap-3">
        <ScheduleEmptyState />
        <Button
          variant="primary"
          size="lg"
          className="gap-2 w-full"
          onClick={() => {
            // Mesmo evento do CTA da lista vazia na sheet.
            tracking.createStarted("empty_state_cta");
            setCreating(true);
          }}
        >
          <Plus className="w-4 h-4" />
          {translate("schedule-new-button")}
        </Button>
      </div>
    );
  }

  // Um block que já chegou sem agendamentos continua não renderizando nada.
  if (items.length === 0) return null;

  // Uma única referência de tempo para todos os itens (igual à sheet).
  const now = new Date();

  return (
    <div className="flex flex-col gap-3">
      {items.map((schedule) => (
        <ScheduleListItem
          key={schedule.id}
          schedule={schedule}
          now={now}
          onEdit={setEditing}
          onToggle={(s) => toggle.mutate({ id: s.id, status: s.status })}
          onDelete={(id) =>
            remove.mutate(id, {
              // Só marca quando era o último: excluir um de vários ainda deixa lista.
              onSuccess: () => {
                if (items.length === 1) setDeletedLast(true);
              },
            })
          }
        />
      ))}
    </div>
  );
}
